import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./about.scss";
import "../landing/join/join.scss";
import Navbar from "../landing/header/Navbar";
import Footer from "../landing/footer/Footer";

const APP_URL = "https://app.traqstock.com";

const marketIntelligence = [
  "Trading activity",
  "Level II and order book information",
  "Time and sales data",
  "Market participant activity",
  "Price and liquidity changes",
  "Historical market behavior",
  "Settlement related information",
  "Shareholder and ownership data where authorized",
  "Repeated patterns and statistical relationships",
];

const shareholderIntelligence = [
  "Shareholder composition",
  "Ownership changes",
  "Concentration trends",
  "Historical ownership patterns",
  "Investor engagement",
  "Changes within the shareholder community",
  "Market activity surrounding significant events",
  "Communication and outreach effectiveness",
];

const companyTags = [
  "Investor relations",
  "Shareholder communications",
  "Market monitoring",
  "Historical research",
  "Corporate decision making",
  "Investor outreach",
  "Audience analysis",
  "Internal reporting",
];

const investorTags = [
  "Organized market data",
  "Analytical context",
  "Historical analytics",
  "Data connected tools",
];

const integrityChips = [
  "Timestamps",
  "Data hashes",
  "Analytical events",
  "Verification records",
  "Provenance information",
  "Historical references",
  "System events",
  "Permissioned and private",
];

const coreCapabilities = [
  "Market activity examined alongside historical information",
  "Shareholder information evaluated over time",
  "Ownership changes compared with market events",
  "Trading patterns preserved and analyzed against history",
];

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.5 8.5L6.5 11.5L12.5 4.5"
      stroke="#6B7280"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const About = () => {
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsMobile(window.innerWidth <= 768);
  }, []);

  return (
    <div className="overflow-hidden">
      <Navbar />

      <section className="aboutbanner">
        <img
          src="/assets/about/aboutbannershade.png"
          alt="img"
          className="bannershade"
        />
        <img
          src={isMobile ? "" : "/assets/banner/layer.png"}
          alt="img"
          className={isMobile ? "d-none" : "layerbg"}
        />
        <video
          className="main-banner-video"
          muted="muted"
          playsInline
          autoPlay
          loop
          width="100%"
        >
          <source src="/assets/banner/bannervideo.mp4.mov" type="video/mp4" />
        </video>
        <div className="custom-container">
          <div className="maincontent">
            <span className="pill">About Traqstock</span>
            <h1 className="head">
              Track Data. Verify Records. Gain Insights.
            </h1>
            <p className="para">
              Traqstock is a financial data intelligence and transparency
              platform that turns fragmented market and shareholder data into
              structured, traceable and actionable information.
            </p>
            <div className="mainbutton">
              <button
                className="dark"
                onClick={() => {
                  window.location.href = APP_URL;
                }}
              >
                Join as Investor
              </button>
              <button className="light" onClick={() => navigate("/contact")}>
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="aboutwhy greysection">
        <img src="/assets/about/backshade.png" alt="" className="backshade" />
        <div className="custom-container">
          <div className="twocol">
            <div className="textcol">
              <span className="pill">Why Traqstock</span>
              <h2>The Problem Isn't A Lack Of Information. It's Connecting It.</h2>
              <p>
                Modern financial markets generate enormous amounts of data.
                Trading activity lives in one environment and shareholder
                information in another. Market data, historical records,
                ownership information, corporate communications and analytical
                signals sit independently, with no effective way to correlate
                what happened, when it happened, and how the datasets relate.
              </p>
              <p>
                Traqstock was built to connect those dots. We aggregate and
                analyze multiple data sources, identify measurable patterns and
                behavioral indicators, preserve historical records, and use
                private blockchain infrastructure to strengthen data integrity
                and provenance.
              </p>
            </div>
            <div className="imgcol">
              <img src="/assets/about/one.png" alt="sphere" className="sphere" />
            </div>
          </div>
        </div>
      </section>

      <section className="aboutconnect">
        <div className="custom-container">
          <h2>
            <span className="muted">We</span> Don't Replace{" "}
            <span className="muted">The Underlying Data.</span> We Connect It.
          </h2>
          <div className="intelcards">
            <div className="intelcard">
              <span className="pill">Market Intelligence</span>
              <h3>A Single Trade Rarely Tells The Whole Story.</h3>
              <p>
                Markets generate millions of individual data points. Traqstock
                examines activity across time and across datasets so market
                behavior can be evaluated in context. Depending on available
                sources, our technology can analyze:
              </p>
              <ul>
                {marketIntelligence.map((item) => (
                  <li key={item}>
                    <CheckIcon />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="intelcard">
              <span className="pill">Shareholder Intelligence</span>
              <h3>Turn A Shareholder List Into Shareholder Intelligence.</h3>
              <p>
                Public companies often struggle to turn shareholder information
                into useful intelligence. Where legally authorized, Traqstock
                connects this information with other datasets to provide
                insights into:
              </p>
              <ul>
                {shareholderIntelligence.map((item) => (
                  <li key={item}>
                    <CheckIcon />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="aboutfeatures">
        <div className="custom-container">
          <div className="twocol">
            <div className="textcol">
              <h2>For companies</h2>
              <p>
                Instead of relying on isolated reports or market snapshots,
                companies can examine relationships among market activity,
                historical information, authorized shareholder data and
                investor engagement. Traqstock turns disconnected information
                into structured corporate intelligence.
              </p>
              <div className="tags">
                {companyTags.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
            </div>
            <div className="imgcol">
              <img src="/assets/about/two.png" alt="submission details" />
            </div>
          </div>

          <div className="twocol reverse">
            <div className="textcol">
              <h2>For investors</h2>
              <p>
                There is no shortage of financial information; the challenge is
                making sense of it. Traqstock organizes relevant data and
                provides analytical context around market activity. Our purpose
                isn't to tell investors what to think. It's to give them better
                information to examine for themselves.
              </p>
              <div className="tags">
                {investorTags.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
            </div>
            <div className="imgcol">
              <img src="/assets/about/three.png" alt="company profile" />
            </div>
          </div>

          <div className="twocol">
            <div className="textcol">
              <h2>A data connected financial community</h2>
              <p>
                Traditional online financial communities suffer from anonymous
                impersonation, spam, automated accounts, coordinated promotion
                and users operating multiple identities. Traqstock builds its
                community infrastructure around greater accountability and
                platform integrity, so financial discussion can exist alongside
                structured market information, historical analytics and data
                driven tools.
              </p>
            </div>
            <div className="imgcol">
              <img src="/assets/about/four.png" alt="community discussion" />
            </div>
          </div>
        </div>
      </section>

      <section className="aboutintegrity greysection">
        <img src="/assets/about/backshade.png" alt="" className="backshade" />
        <div className="custom-container">
          <div className="centertext">
            <span className="pill">Data Integrity</span>
            <h2>
              Data should not only be analyzed. Its history should be
              verifiable.
            </h2>
            <p>
              Traqstock uses a permissioned Hyperledger Fabric private
              blockchain as part of its data integrity architecture. Information
              can be analyzed today while keeping cryptographic evidence of when
              the underlying record was captured or created. The blockchain
              preserves:
            </p>
            <div className="chips">
              {integrityChips.map((chip) => (
                <span key={chip}>{chip}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="aboutcore">
        <div className="custom-container">
          <div className="twocol">
            <div className="textcol">
              <span className="pill">The core capability</span>
              <h2>It's Not More Data. It's Connected Data.</h2>
              <p>
                One of Traqstock's core capabilities is establishing
                relationships between datasets that have traditionally existed
                separately. When authorized datasets are combined, a company can
                build a far more complete picture of the environment surrounding
                its security.
              </p>
            </div>
            <div className="corelist">
              {coreCapabilities.map((item) => (
                <div key={item} className="coreitem">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="abouttrust greysection">
        <img src="/assets/about/backshade.png" alt="" className="backshade" />
        <div className="custom-container">
          <div className="centertext">
            <span className="pill">Infrastructure</span>
            <h2>Designed For Trust.</h2>
            <p>
              Traqstock combines modern cloud infrastructure with permissioned
              blockchain technology, letting operational information and
              integrity records serve different purposes while working together
              as part of a broader data environment.
            </p>
          </div>
          <div className="trustcards">
            <div className="trustcard">
              <span className="awslogo">aws</span>
              <h3>Amazon Web Services</h3>
              <p>
                Modern, scalable cloud infrastructure that handles operational
                data, aggregation and analysis across the platform.
              </p>
            </div>
            <span className="plus">+</span>
            <div className="trustcard">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="#0F0F15"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 1.5L21.5 6.75V17.25L12 22.5L2.5 17.25V6.75L12 1.5Z" />
                <path
                  d="M2.5 6.75L12 12L21.5 6.75M12 12V22.5"
                  stroke="#fff"
                  strokeWidth="1.2"
                  fill="none"
                />
              </svg>
              <h3>Hyperledger Fabric</h3>
              <p>
                A private, permissioned blockchain that preserves cryptographic
                evidence and provenance rather than operating as an unrestricted
                public chain.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="aboutconnects">
        <div className="custom-container">
          <div className="centertext">
            <span className="pill">More than data aggregation</span>
            <h2>Intelligence Begins When Data Connects.</h2>
            <p>
              Collecting data is only the beginning. Traqstock creates
              relationships between information that traditionally lives in
              separate systems
            </p>
          </div>
          <img
            src="/assets/about/five.png"
            alt="Traqstock data connections"
            className="diagram"
          />
        </div>
      </section>

      <section className="mainjoin">
        <img
          src="/assets/about/bottomshade.png"
          alt="bottomshade"
          className="darkershade"
        />
        <video
          className="mainjoinvideo"
          muted="muted"
          playsInline
          autoPlay
          loop
          width="100%"
        >
          <source src="/assets/join/join.mp4" type="video/mp4" />
        </video>
        <div className="custom-container">
          <div className="maincontenttext">
            <span>Get Started</span>
            <h2>Join the Future of Transparent Market Data</h2>
            <p>
              Join early access, or reach out to bring verified, connected
              financial intelligence to your company and shareholders.
            </p>
            <div className="lastbutton">
              <button
                className="acess"
                onClick={() => {
                  window.location.href = APP_URL;
                }}
              >
                Join as Investor
              </button>
              <button className="early" onClick={() => navigate("/contact")}>
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
